/**
 * CrmDublettenPage — Admin-UI für Kontakt-Dubletten-Merge (#383, Epic 6 Sub 5).
 *
 * - Liste der Kandidaten-Paare aus crm_kontakt_dubletten_admin (sortiert nach Score)
 * - Filter nach match_reason, Sortierung nach Score
 * - Side-by-Side-Wizard: Master-Auswahl, Konfliktauflösung, Vorschau, Merge
 */
import { useMemo, useState } from 'react';
import { ArrowLeft, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { Link } from 'wouter';
import { toast } from 'sonner';
import { useDublettenMerge, type DublettenMatchReason, type DublettenPaar } from '@/hooks/useDublettenMerge';
import {
  MERGEABLE_FIELDS,
  buildFieldOverrides,
  buildMergePreview,
  defaultDecisions,
  detectConflicts,
  type Decisions,
  type MergeableField,
} from '@/lib/crm-dubletten-merge';
import type { CRMKontakt } from '@/hooks/useCRM';

const REASON_LABEL: Record<DublettenMatchReason, string> = {
  email: 'E-Mail',
  phone: 'Telefon',
  fuzzy: 'Name/Adresse',
};

const FIELD_LABEL: Record<MergeableField, string> = {
  name: 'Name',
  firma: 'Firma',
  email: 'E-Mail',
  telefon: 'Telefon',
  mobil: 'Mobil',
  adresse: 'Adresse',
  plz: 'PLZ',
  ort: 'Ort',
  webseite: 'Webseite',
  position: 'Position',
  notizen: 'Notizen',
};

function ScoreBadge({ score }: { score: number }) {
  const tone =
    score >= 100
      ? 'bg-red-50 text-red-700 border-red-200'
      : score >= 90
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : 'bg-yellow-50 text-yellow-700 border-yellow-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded ${tone}`}>
      {score}
    </span>
  );
}

function ReasonBadge({ reason }: { reason: DublettenMatchReason }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded bg-gray-50 text-gray-700 border-gray-200">
      {REASON_LABEL[reason]}
    </span>
  );
}

interface MergeWizardProps {
  paar: DublettenPaar;
  kontaktA: CRMKontakt;
  kontaktB: CRMKontakt;
  onCancel: () => void;
  onMerged: () => void;
  onMerge: (
    masterId: string,
    duplicateId: string,
    overrides: Record<string, string>,
  ) => Promise<string>;
}

function MergeWizard({ paar, kontaktA, kontaktB, onCancel, onMerged, onMerge }: MergeWizardProps) {
  const [masterId, setMasterId] = useState<string>(kontaktA.id);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const master = masterId === kontaktA.id ? kontaktA : kontaktB;
  const duplicate = masterId === kontaktA.id ? kontaktB : kontaktA;

  const [decisions, setDecisions] = useState<Decisions>(() => defaultDecisions(master, duplicate));

  // Recompute defaults when master flips
  const handleMasterSwap = (newMasterId: string) => {
    setMasterId(newMasterId);
    const newMaster = newMasterId === kontaktA.id ? kontaktA : kontaktB;
    const newDup = newMasterId === kontaktA.id ? kontaktB : kontaktA;
    setDecisions(defaultDecisions(newMaster, newDup));
  };

  const conflicts = useMemo(() => detectConflicts(master, duplicate), [master, duplicate]);
  const preview = useMemo(
    () => buildMergePreview(master, duplicate, decisions),
    [master, duplicate, decisions],
  );

  const setFieldChoice = (field: MergeableField, choice: 'master' | 'duplicate' | 'custom') => {
    setDecisions((prev) => ({ ...prev, [field]: { ...prev[field], choice } }));
  };
  const setCustomValue = (field: MergeableField, value: string) => {
    setDecisions((prev) => ({ ...prev, [field]: { choice: 'custom', customValue: value } }));
  };

  const submit = async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const overrides = buildFieldOverrides(master, duplicate, decisions);
      await onMerge(master.id, duplicate.id, overrides);
      toast.success(`Kontakte zusammengeführt — Master: ${master.name || master.id}`);
      onMerged();
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setErrorMsg(msg);
      toast.error(`Merge fehlgeschlagen: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="merge-wizard"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Zurück zur Liste
            </button>
            <h2 className="text-lg font-semibold text-gray-900">Dublette zusammenführen</h2>
          </div>
          <div className="flex items-center gap-2">
            <ReasonBadge reason={paar.match_reason} />
            <ScoreBadge score={paar.score} />
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Master-Kontakt (bleibt bestehen)</label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {[kontaktA, kontaktB].map((k) => (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => handleMasterSwap(k.id)}
                  data-testid={`master-option-${k.id}`}
                  className={`text-left border rounded p-3 transition ${
                    masterId === k.id
                      ? 'border-[#B91C1C] bg-red-50 ring-1 ring-[#B91C1C]'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-900">{k.name || '—'}</div>
                  <div className="text-xs text-gray-600">{k.firma || ''}</div>
                  <div className="text-xs text-gray-500 mt-1">{k.email || k.telefon || ''}</div>
                  {masterId === k.id && (
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-[#B91C1C] font-medium">
                      <ShieldCheck className="w-3 h-3" /> Master
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                <tr>
                  <th className="text-left px-3 py-2 w-32">Feld</th>
                  <th className="text-left px-3 py-2">Master</th>
                  <th className="text-left px-3 py-2">Duplikat</th>
                  <th className="text-left px-3 py-2 w-56">Auswahl</th>
                  <th className="text-left px-3 py-2">Vorschau</th>
                </tr>
              </thead>
              <tbody>
                {MERGEABLE_FIELDS.map((field) => {
                  const conflict = conflicts.find((c) => c.field === field)!;
                  const decision = decisions[field];
                  return (
                    <tr
                      key={field}
                      data-testid={`row-${field}`}
                      className={conflict.isConflict ? 'bg-yellow-50' : ''}
                    >
                      <td className="px-3 py-2 align-top text-gray-600 font-medium">
                        {FIELD_LABEL[field]}
                      </td>
                      <td className="px-3 py-2 align-top text-gray-900 break-words">
                        {conflict.masterValue || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 align-top text-gray-900 break-words">
                        {conflict.duplicateValue || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col gap-1 text-xs">
                          <label className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name={`choice-${field}`}
                              checked={decision.choice === 'master'}
                              onChange={() => setFieldChoice(field, 'master')}
                            />
                            Master
                          </label>
                          <label className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name={`choice-${field}`}
                              checked={decision.choice === 'duplicate'}
                              onChange={() => setFieldChoice(field, 'duplicate')}
                            />
                            Duplikat
                          </label>
                          <label className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name={`choice-${field}`}
                              checked={decision.choice === 'custom'}
                              onChange={() => setFieldChoice(field, 'custom')}
                            />
                            Eigener Wert
                          </label>
                          {decision.choice === 'custom' && (
                            <input
                              type="text"
                              value={decision.customValue ?? ''}
                              onChange={(e) => setCustomValue(field, e.target.value)}
                              className="border rounded px-2 py-1 text-xs"
                              placeholder="eigenen Wert eingeben"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-gray-900 font-medium break-words">
                        {preview[field] || <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {errorMsg && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm border rounded text-gray-700 hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="submit-merge"
            className="px-4 py-2 text-sm rounded bg-[#B91C1C] text-white hover:bg-[#991B1B] disabled:opacity-60 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Mergen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CrmDublettenPage() {
  const { paare, kontakteById, loading, loadingMore, hasMore, error, fetch, loadMore, merge } =
    useDublettenMerge();
  const [reasonFilter, setReasonFilter] = useState<'all' | DublettenMatchReason>('all');
  const [activePaar, setActivePaar] = useState<DublettenPaar | null>(null);

  const filtered = useMemo(
    () => (reasonFilter === 'all' ? paare : paare.filter((p) => p.match_reason === reasonFilter)),
    [paare, reasonFilter],
  );

  const activeKontakte = useMemo(() => {
    if (!activePaar) return null;
    const a = kontakteById[activePaar.kontakt_a_id];
    const b = kontakteById[activePaar.kontakt_b_id];
    if (!a || !b) return null;
    return { a, b };
  }, [activePaar, kontakteById]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link href="/crm/kontakte" className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> CRM
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900 mt-1">Dubletten zusammenführen</h1>
          <p className="text-sm text-gray-500 mt-1">
            Kandidatenpaare aus der nächtlichen Erkennung. Sortiert nach Score (höchster zuerst).
          </p>
        </div>
        <button
          onClick={fetch}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border rounded text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" /> Neu laden
        </button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {(['all', 'email', 'phone', 'fuzzy'] as const).map((r) => (
          <button
            key={r}
            onClick={() => setReasonFilter(r)}
            data-testid={`filter-${r}`}
            className={`text-xs px-3 py-1 rounded border ${
              reasonFilter === r
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {r === 'all' ? 'Alle' : REASON_LABEL[r]}
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-2">{filtered.length} Paar(e)</span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Dubletten…
        </div>
      )}

      {error && !loading && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          Fehler beim Laden: {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="border border-dashed rounded p-12 text-center text-gray-500 text-sm">
          Keine Dubletten gefunden.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Kontakt A</th>
                <th className="text-left px-3 py-2">Kontakt B</th>
                <th className="text-left px-3 py-2 w-28">Grund</th>
                <th className="text-left px-3 py-2 w-20">Score</th>
                <th className="text-right px-3 py-2 w-32">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const a = kontakteById[p.kontakt_a_id];
                const b = kontakteById[p.kontakt_b_id];
                const key = `${p.kontakt_a_id}-${p.kontakt_b_id}-${p.match_reason}`;
                return (
                  <tr key={key} data-testid={`paar-${key}`} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-gray-900">{a?.name ?? '—'}</div>
                      <div className="text-xs text-gray-500">{a?.email || a?.telefon || ''}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-gray-900">{b?.name ?? '—'}</div>
                      <div className="text-xs text-gray-500">{b?.email || b?.telefon || ''}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <ReasonBadge reason={p.match_reason} />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <ScoreBadge score={p.score} />
                    </td>
                    <td className="px-3 py-2 align-top text-right">
                      <button
                        onClick={() => setActivePaar(p)}
                        disabled={!a || !b}
                        data-testid={`pruefen-${key}`}
                        className="px-3 py-1 text-xs border rounded text-gray-800 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Prüfen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasMore && (
            <div className="flex justify-center py-3 border-t bg-gray-50">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="load-more"
                className="inline-flex items-center gap-2 text-sm px-3 py-1.5 border rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Mehr laden
              </button>
            </div>
          )}
        </div>
      )}

      {activePaar && activeKontakte && (
        <MergeWizard
          paar={activePaar}
          kontaktA={activeKontakte.a}
          kontaktB={activeKontakte.b}
          onCancel={() => setActivePaar(null)}
          onMerged={() => setActivePaar(null)}
          onMerge={merge}
        />
      )}
    </div>
  );
}
